import { useCallback, useMemo, useState } from "react"
import {
	IconTrash2Outline18 as Trash2Icon,
	IconArrowTurnLeftOutline18 as UndoIcon,
} from "nucleo-ui-outline-18"

import { AlertDialog, Button, Dialog, IconButton } from "@productlane/ui"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"

interface ContactImport {
	email: string
	name?: string
	imageUrl?: string
	isSubscribed?: boolean
}

interface ContactsPreviewDialogProps {
	isOpen: boolean
	onOpenChange: () => void
	onSuccess: () => void
	hasHeaders: boolean
	sortedData: string[][]
	contactEmailIndex: number
	contactNameIndex: number
	imageUrlIndex: number
	subscribedIndex: number
	subscribeUnmapped: boolean
}

const parseBooleanLoose = (value: string | undefined): boolean | undefined => {
	if (!value) return undefined
	const v = value.trim().toLowerCase()
	if (["true", "1", "yes", "y"].includes(v)) return true
	if (["false", "0", "no", "n"].includes(v)) return false
	return undefined
}

export function ContactsPreviewDialog({
	isOpen,
	onOpenChange,
	onSuccess,
	hasHeaders,
	sortedData,
	contactEmailIndex,
	contactNameIndex,
	imageUrlIndex,
	subscribedIndex,
	subscribeUnmapped,
}: ContactsPreviewDialogProps) {
	const [deleted, setDeleted] = useState<number[]>([])
	const [isConfirmCloseDialogOpen, setIsConfirmCloseDialogOpen] =
		useState(false)

	const contacts = useMemo<ContactImport[]>(() => {
		if (contactEmailIndex === -1) return []
		const emailColumn = sortedData[contactEmailIndex] ?? []
		const start = hasHeaders ? 1 : 0
		const length = Math.max(emailColumn.length - start, 0)

		const result: ContactImport[] = []
		for (let i = 0; i < length; i++) {
			const rowIndex = start + i
			const email = (sortedData[contactEmailIndex]?.[rowIndex] ?? "").trim()
			if (!email) continue

			const name =
				contactNameIndex === -1
					? undefined
					: (sortedData[contactNameIndex]?.[rowIndex] ?? "").trim() || undefined

			const imageUrl =
				imageUrlIndex === -1
					? undefined
					: (sortedData[imageUrlIndex]?.[rowIndex] ?? "").trim() || undefined

			const isSubscribed =
				subscribedIndex === -1
					? subscribeUnmapped
					: (parseBooleanLoose(sortedData[subscribedIndex]?.[rowIndex]) ??
						subscribeUnmapped)

			result.push({ email, name, imageUrl, isSubscribed })
		}

		return result
	}, [
		contactEmailIndex,
		contactNameIndex,
		hasHeaders,
		imageUrlIndex,
		sortedData,
		subscribedIndex,
		subscribeUnmapped,
	])

	const importMutation = api.workspace.importContactsCsv.useMutation({
		onSuccess: (result) => {
			toast.success({
				title: "Contacts imported",
				description: `Created ${result.created}, updated ${result.updated}, skipped ${result.skipped}.`,
			})
			onSuccess()
			onOpenChange()
			setDeleted([])
		},
		onError: (error) => {
			toast.error({
				title: "Import failed",
				description: error.message || "Failed to import contacts.",
			})
		},
	})

	const handleDelete = useCallback(
		(i: number) => {
			if (deleted.includes(i)) {
				setDeleted(deleted.filter((it) => it !== i))
			} else {
				setDeleted([...deleted, i])
			}
		},
		[deleted],
	)

	const handleUpload = useCallback(() => {
		const uploadContacts = contacts.filter((_, i) => !deleted.includes(i))
		analytics.track("upload-contacts-csv")
		importMutation.mutate({
			contacts: uploadContacts,
		})
	}, [contacts, deleted, importMutation])

	const handleOpenChange = useCallback(() => {
		if (importMutation.isLoading) {
			setIsConfirmCloseDialogOpen(true)
		} else {
			onOpenChange()
		}
	}, [importMutation.isLoading, onOpenChange])

	const handleConfirmClose = useCallback(() => {
		setIsConfirmCloseDialogOpen(false)
		onOpenChange()
	}, [onOpenChange])

	return (
		<>
			<AlertDialog
				isOpen={isConfirmCloseDialogOpen}
				onOpenChange={setIsConfirmCloseDialogOpen}
				title="Upload in progress"
				description="The contacts upload is currently in progress. Are you sure you want to cancel and close?"
				confirmText="Stop & Close"
				cancelText="Continue Upload"
				onConfirm={handleConfirmClose}
				onCancel={() => setIsConfirmCloseDialogOpen(false)}
			/>

			<Dialog
				title="Preview"
				description="Please remove contacts you might not want to upload"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				footer={
					<div>
						<Button isLoading={importMutation.isLoading} onClick={handleUpload}>
							Upload
						</Button>
					</div>
				}
			>
				<div className="space-y-2">
					{contacts.map((c, i) => (
						<div
							key={`${c.email}-${i}`}
							className="relative flex flex-col overflow-visible rounded-lg border p-2.5 text-sm text-secondary"
						>
							<div className="flex items-center gap-2 border-b pb-2">
								<div className="flex-1 truncate">
									<div className="font-medium text-primary">{c.email}</div>
									{c.name && (
										<div className="text-xs text-secondary">{c.name}</div>
									)}
									{c.isSubscribed !== undefined && (
										<div className="text-xs text-secondary">
											Subscribed: {c.isSubscribed ? "true" : "false"}
										</div>
									)}
									{c.imageUrl && (
										<div className="truncate text-xs text-green-500">
											Image: {c.imageUrl}
										</div>
									)}
								</div>
								<IconButton
									className="z-10 ml-auto"
									onClick={() => handleDelete(i)}
									variant="ghost"
									Icon={deleted.includes(i) ? UndoIcon : Trash2Icon}
								/>
							</div>

							{deleted.includes(i) && (
								<div className="absolute inset-0 z-1 bg-white/70 dark:bg-gray-900/70" />
							)}
						</div>
					))}
				</div>
			</Dialog>
		</>
	)
}
